import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { TenantConfig, Theme } from '../types'
import { kebabCase } from '../utils'

interface TenantContextValue {
  tenant: TenantConfig | null
  isLoading: boolean
  error: string | null
  t: (key: string, fallback?: string) => string
}

const TenantContext = createContext<TenantContextValue | null>(null)

function hexToHSL(hex: string): string {
  hex = hex.replace('#', '')

  const r = parseInt(hex.slice(0, 2), 16) / 255
  const g = parseInt(hex.slice(2, 4), 16) / 255
  const b = parseInt(hex.slice(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / d + 2) / 6
        break
      case b:
        h = ((r - g) / d + 4) / 6
        break
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}

function injectThemeVariables(theme: Theme): void {
  const root = document.documentElement

  Object.entries(theme.colors).forEach(([key, value]) => {
    const cssVarName = `--color-${kebabCase(key)}`
    const hslValue = value.startsWith('#') ? hexToHSL(value) : value
    root.style.setProperty(cssVarName, hslValue)
  })

  root.style.setProperty('--font-heading', theme.fonts.heading)
  root.style.setProperty('--font-body', theme.fonts.body)
  root.style.setProperty('--font-mono', theme.fonts.mono)

  const radiusMap = {
    none: '0px',
    sm: '0.25rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    full: '9999px',
  }
  root.style.setProperty('--radius', radiusMap[theme.radius])

  const spacingMap = {
    compact: '0.75',
    normal: '1',
    relaxed: '1.25',
  }
  root.style.setProperty('--spacing-multiplier', spacingMap[theme.spacing])
}

function injectFavicon(faviconUrl: string): void {
  let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  link.href = faviconUrl
}

function injectMetaTags(tenant: TenantConfig): void {
  document.title = tenant.name

  const updateMeta = (name: string, content: string) => {
    let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement
    if (!meta) {
      meta = document.createElement('meta')
      meta.name = name
      document.head.appendChild(meta)
    }
    meta.content = content
  }

  const updateOgMeta = (property: string, content: string) => {
    let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement
    if (!meta) {
      meta = document.createElement('meta')
      meta.setAttribute('property', property)
      document.head.appendChild(meta)
    }
    meta.content = content
  }

  updateMeta('description', tenant.description)
  updateOgMeta('og:title', tenant.name)
  updateOgMeta('og:description', tenant.description)
  updateOgMeta('og:image', tenant.branding.ogImage)
  updateMeta('theme-color', tenant.theme.colors.primary)
}

interface TenantProviderProps {
  tenant: TenantConfig
  children: ReactNode
}

export function TenantProvider({ tenant, children }: TenantProviderProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [error] = useState<string | null>(null)

  useEffect(() => {
    if (tenant) {
      injectThemeVariables(tenant.theme)
      injectFavicon(tenant.branding.favicon)
      injectMetaTags(tenant)
      setIsLoading(false)
    }
  }, [tenant])

  const t = (key: string, fallback?: string): string => {
    if (!tenant) return fallback ?? key

    const locale = tenant.i18n.defaultLocale
    const translations = tenant.i18n.translations[locale]

    if (translations && key in translations) {
      return translations[key]
    }

    return fallback ?? key
  }

  return (
    <TenantContext.Provider value={{ tenant, isLoading, error, t }}>
      {children}
    </TenantContext.Provider>
  )
}

export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext)
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider')
  }
  return context
}

export function useTenantConfig(): TenantConfig {
  const { tenant } = useTenant()
  if (!tenant) {
    throw new Error('Tenant not loaded')
  }
  return tenant
}

export function useTranslation() {
  const { t } = useTenant()
  return { t }
}
